import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DIALOG_WIDTH_SM, UNGROUPED_CHARGERS_PAGE_SIZE } from '../../app.constants';
import { AuthService } from '../../auth/auth.service';
import { ChargerAddDialogComponent } from '../../chargers/charger-add-dialog/charger-add-dialog.component';
import { ChargersService } from '../../chargers/chargers.service';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { NotificationService } from '../../main/notification.service';
import { ChargingStateTranslatePipe } from '../../main/translate/charging-state-translate.pipe';
import { ChargerOverview, ChargerSearchResult, Group } from '../../models';
import { GroupDialogComponent, GroupDialogData } from '../group-dialog/group-dialog.component';
import { GroupsService } from '../groups.service';
import { MoveChargerDialogComponent, MoveChargerDialogData } from '../move-charger-dialog/move-charger-dialog.component';

export interface GroupNode extends Group {
	kind: 'group';
	expanded: boolean;
	loading: boolean;
	childrenLoaded: boolean;
}

export interface ChargerNode {
	kind: 'charger';
	chargerId: string;
	groupId: string;
	level: number;
	cloudConnectionState?: ChargerOverview['cloudConnectionState'];
	chargingState?: ChargerOverview['chargerStatus']['indicators']['chargingState'];
}

export interface SentinelNode {
	kind: 'sentinel';
	groupId: string;
	level: number;
}

export type TreeNode = GroupNode | ChargerNode | SentinelNode;

@Component({
	selector: 'app-my-groups',
	imports: [MatProgressSpinnerModule, MatIconModule, RouterLink, MatButtonModule, MatCardModule, MatMenuModule, MatTooltip, ChargingStateTranslatePipe, MatFormFieldModule, MatInputModule],
	templateUrl: './my-groups.component.html',
	styleUrl: './my-groups.component.scss'
})
export class MyGroupsComponent implements OnInit, OnDestroy {
	treeNodes = signal<TreeNode[]>([]);
	loaded = signal(false);
	searchQuery = signal('');
	searchLoading = signal(false);
	highlightedChargerIds = signal<Set<string>>(new Set());
	ungroupedLoadingMore = signal(false);
	private searchTimeout: ReturnType<typeof setTimeout> | null = null;

	readonly UNGROUPED_ID = '__ungrouped__';

	private searchExpandedGroupIds = new Set<string>();
	private childrenCache = new Map<string, GroupNode[]>();
	private chargersCache = new Map<string, ChargerNode[]>();
	private ungroupedNextToken: string | null = null;
	private sentinelObserver?: IntersectionObserver;

	private groupsService = inject(GroupsService);
	private chargersService = inject(ChargersService);
	private notificationService = inject(NotificationService);
	private dialog = inject(MatDialog);
	public authService = inject(AuthService);

	ngOnInit() {
		this.loadGroups();
	}

	ngOnDestroy() {
		this.sentinelObserver?.disconnect();
	}

	async loadGroups() {
		this.loaded.set(false);
		this.childrenCache.clear();
		this.chargersCache.clear();
		this.ungroupedNextToken = null;
		this.groupsService.getGroups().subscribe(groups => {
			groups.sort((a, b) => a.name.localeCompare(b.name));
			const ungroupedNode: GroupNode = {
				kind: 'group',
				groupId: this.UNGROUPED_ID,
				name: $localize`:@@Ungrouped:Ungrouped`,
				parentGroupId: null,
				path: 'Ungrouped',
				pathIds: [],
				level: 0,
				expanded: false,
				loading: false,
				childrenLoaded: false,
			};
			this.treeNodes.set([...groups.map(g => this.toGroupNode(g)), ungroupedNode]);
			this.loaded.set(true);
		});
	}

	async toggleExpand(node: GroupNode) {
		if (node.loading) return;

		const nodes = [...this.treeNodes()];
		const index = nodes.findIndex(n => n.kind === 'group' && n.groupId === node.groupId);
		if (index === -1) return;

		if (node.expanded) {
			const count = this.countDescendants(nodes, index);
			nodes.splice(index + 1, count);
			nodes[index] = { ...nodes[index], expanded: false } as GroupNode;
			if (node.groupId === this.UNGROUPED_ID) {
				this.ungroupedNextToken = null;
				this.chargersCache.delete(this.UNGROUPED_ID);
				this.sentinelObserver?.disconnect();
			}
			this.treeNodes.set(nodes);
			return;
		}

		// Load children and chargers from cache or API in parallel
		const isUngrouped = node.groupId === this.UNGROUPED_ID;
		const needsGroups = !isUngrouped && !this.childrenCache.has(node.groupId);
		const needsChargers = !this.chargersCache.has(node.groupId);

		if (needsGroups || needsChargers) {
			nodes[index] = { ...nodes[index], loading: true } as GroupNode;
			this.treeNodes.set([...nodes]);

			const loadChargers = isUngrouped
				? firstValueFrom(this.groupsService.getUngroupedChargersOverview(UNGROUPED_CHARGERS_PAGE_SIZE)).then(response => {
					this.ungroupedNextToken = response.nextToken ?? null;
					this.chargersCache.set(this.UNGROUPED_ID, response.chargers.map(c => ({
						kind: 'charger' as const,
						chargerId: c.serialNumber,
						groupId: this.UNGROUPED_ID,
						level: 1,
						cloudConnectionState: c.cloudConnectionState,
						chargingState: c.chargerStatus?.indicators?.chargingState,
					})));
				})
				: firstValueFrom(this.groupsService.getGroupChargersOverview(node.groupId)).then(chargers => {
					chargers.sort((a, b) => a.serialNumber.localeCompare(b.serialNumber));
					this.chargersCache.set(node.groupId, chargers.map(c => ({
						kind: 'charger' as const,
						chargerId: c.serialNumber,
						groupId: node.groupId,
						level: Number(node.level) + 1,
						cloudConnectionState: c.cloudConnectionState,
						chargingState: c.chargerStatus?.indicators?.chargingState,
					})));
				});

			await Promise.all([
				needsGroups ? firstValueFrom(this.groupsService.getChildGroups(node.groupId)).then(children => {
					children.sort((a, b) => a.name.localeCompare(b.name));
					this.childrenCache.set(node.groupId, children.map(c => this.toGroupNode(c)));
				}) : Promise.resolve(),
				needsChargers ? loadChargers : Promise.resolve(),
			]);
		}

		const childGroupNodes = isUngrouped ? [] : (this.childrenCache.get(node.groupId) ?? []).map(c => ({ ...c, expanded: false }));
		const chargerNodes = this.chargersCache.get(node.groupId) ?? [];
		const toInsert: TreeNode[] = [...chargerNodes, ...childGroupNodes];
		if (isUngrouped && this.ungroupedNextToken) {
			toInsert.push({ kind: 'sentinel', groupId: this.UNGROUPED_ID, level: 1 });
		}

		const fresh = [...this.treeNodes()];
		const freshIndex = fresh.findIndex(n => n.kind === 'group' && (n as GroupNode).groupId === node.groupId);
		fresh.splice(freshIndex + 1, 0, ...toInsert);
		fresh[freshIndex] = { ...fresh[freshIndex], loading: false, expanded: true, childrenLoaded: true } as GroupNode;
		this.treeNodes.set(fresh);
		if (isUngrouped && this.ungroupedNextToken) {
			this.connectSentinelObserver();
		}
	}

	async loadMoreUngrouped() {
		if (this.ungroupedLoadingMore() || !this.ungroupedNextToken) return;
		this.ungroupedLoadingMore.set(true);
		try {
			const response = await firstValueFrom(
				this.groupsService.getUngroupedChargersOverview(UNGROUPED_CHARGERS_PAGE_SIZE, this.ungroupedNextToken)
			);
			this.ungroupedNextToken = response.nextToken ?? null;
			const newChargers: ChargerNode[] = response.chargers.map(c => ({
				kind: 'charger' as const,
				chargerId: c.serialNumber,
				groupId: this.UNGROUPED_ID,
				level: 1,
				cloudConnectionState: c.cloudConnectionState,
				chargingState: c.chargerStatus?.indicators?.chargingState,
			}));
			const nodes = [...this.treeNodes()];
			const sentinelIndex = nodes.findIndex(n => n.kind === 'sentinel' && n.groupId === this.UNGROUPED_ID);
			if (sentinelIndex !== -1) nodes.splice(sentinelIndex, 1);
			const insertAt = sentinelIndex !== -1 ? sentinelIndex : nodes.length;
			nodes.splice(insertAt, 0, ...newChargers);
			if (this.ungroupedNextToken) {
				nodes.splice(insertAt + newChargers.length, 0, { kind: 'sentinel', groupId: this.UNGROUPED_ID, level: 1 });
			}
			this.treeNodes.set(nodes);
			if (this.ungroupedNextToken) {
				this.connectSentinelObserver();
			}
		} finally {
			this.ungroupedLoadingMore.set(false);
		}
	}

	private connectSentinelObserver() {
		this.sentinelObserver?.disconnect();
		if (typeof IntersectionObserver === 'undefined') return;
		this.sentinelObserver = new IntersectionObserver(
			(entries) => {
				if (entries.some(e => e.isIntersecting) && !this.ungroupedLoadingMore()) {
					this.loadMoreUngrouped();
				}
			},
			{ rootMargin: '200px' }
		);
		requestAnimationFrame(() => {
			const el = document.querySelector('.ungrouped-sentinel');
			if (el) this.sentinelObserver?.observe(el);
		});
	}

	private countDescendants(nodes: TreeNode[], parentIndex: number): number {
		const parentLevel = Number(nodes[parentIndex].level);
		let count = 0;
		for (let i = parentIndex + 1; i < nodes.length; i++) {
			if (Number(nodes[i].level) <= parentLevel) break;
			count++;
		}
		return count;
	}

	private toGroupNode(g: Group): GroupNode {
		return { ...g, level: Number(g.level), kind: 'group', expanded: false, loading: false, childrenLoaded: false };
	}

	hasKnownChildren(node: GroupNode): boolean {
		const groupsLoaded = this.childrenCache.has(node.groupId);
		const chargersLoaded = this.chargersCache.has(node.groupId);
		if (!groupsLoaded || !chargersLoaded) return true; // unknown → show button
		return (this.childrenCache.get(node.groupId)!.length > 0)
			|| (this.chargersCache.get(node.groupId)!.length > 0);
	}

	asGroup(node: TreeNode): GroupNode {
		return node as GroupNode;
	}

	asCharger(node: TreeNode): ChargerNode {
		return node as ChargerNode;
	}

	trackNode(_: number, node: TreeNode): string {
		if (node.kind === 'sentinel') return `sentinel:${node.groupId}`;
		return node.kind === 'group' ? `group:${node.groupId}` : `charger:${node.groupId}:${node.chargerId}`;
	}

	onSearchInput(value: string): void {
		this.searchQuery.set(value);
		if (this.searchTimeout) clearTimeout(this.searchTimeout);

		const digits = (value.match(/\d/g) ?? []).join('');
		if (digits.length < 3 || digits === '000') {
			this.highlightedChargerIds.set(new Set());
			return;
		}
		this.searchTimeout = setTimeout(() => this.runSearch(value), 400);
	}

	private async runSearch(q: string): Promise<void> {
		this.searchLoading.set(true);
		try {
			const results = await firstValueFrom(this.chargersService.searchChargers(q));
			if (q !== this.searchQuery()) return;
			this.highlightedChargerIds.set(new Set(results.map((r: ChargerSearchResult) => r.serialNumber)));

			// Fetch group hierarchies once and compute all group IDs needed for new results
			const hierarchyCache = new Map<string, Group[]>();
			const neededGroupIds = new Set<string>();
			for (const charger of results) {
				if (charger.groups.length === 0) {
					neededGroupIds.add(this.UNGROUPED_ID);
				} else {
					for (const group of charger.groups) {
						if (!hierarchyCache.has(group.groupId)) {
							const hierarchy = await firstValueFrom(this.groupsService.getGroupHierarchy(group.groupId));
							hierarchyCache.set(group.groupId, [...hierarchy.ancestors, hierarchy.group]);
						}
						hierarchyCache.get(group.groupId)!.forEach(g => neededGroupIds.add(g.groupId));
					}
				}
			}

			// Collapse search-expanded groups that are no longer needed
			for (const groupId of [...this.searchExpandedGroupIds]) {
				if (!neededGroupIds.has(groupId)) {
					const node = this.treeNodes().find(
						n => n.kind === 'group' && (n as GroupNode).groupId === groupId
					) as GroupNode | undefined;
					if (node?.expanded) await this.toggleExpand(node);
					this.searchExpandedGroupIds.delete(groupId);
				}
			}

			// Expand groups needed for new results, skipping already-expanded ones
			for (const charger of results) {
				if (charger.groups.length === 0) {
					const ungroupedNode = this.treeNodes().find(
						n => n.kind === 'group' && (n as GroupNode).groupId === this.UNGROUPED_ID
					) as GroupNode | undefined;
					if (ungroupedNode && !ungroupedNode.expanded) {
						await this.toggleExpand(ungroupedNode);
						this.searchExpandedGroupIds.add(this.UNGROUPED_ID);
					}
				} else {
					for (const group of charger.groups) {
						for (const g of hierarchyCache.get(group.groupId)!) {
							const node = this.treeNodes().find(
								n => n.kind === 'group' && (n as GroupNode).groupId === g.groupId
							) as GroupNode | undefined;
							if (node && !node.expanded) {
								await this.toggleExpand(node);
								this.searchExpandedGroupIds.add(g.groupId);
							}
						}
					}
				}
			}
		} finally {
			this.searchLoading.set(false);
		}
	}

	async openAssignChargerDialog(event: MouseEvent, node: GroupNode) {
		event.stopPropagation();

		const ref = this.dialog.open(ChargerAddDialogComponent, {
			width: DIALOG_WIDTH_SM,
			disableClose: true,
		});

		const result = await firstValueFrom(ref.afterClosed());
		if (!result) return;

		try {
			const addResult = await firstValueFrom(this.chargersService.putChargerAdd(result.serialNumber, result.bluetoothPasskey));
			if (!addResult.success) {
				this.notificationService.openSnackbar(false, addResult.errorMessage, $localize`:@@AddCharger:Add Charger`);
				return;
			}

			const assignResult = await firstValueFrom(this.groupsService.assignChargersToGroup(node.groupId, [result.serialNumber]));
			if (!assignResult.success) {
				this.notificationService.openSnackbar(false, assignResult.errorMessage, $localize`:@@AddCharger:Add Charger`);
				return;
			}

			this.notificationService.openSnackbar(true, null, $localize`:@@AddCharger:Add Charger`);
			// Invalidate cache and reload children if expanded
			this.chargersCache.delete(node.groupId);
			const nodes = [...this.treeNodes()];
			const index = nodes.findIndex(n => n.kind === 'group' && (n as GroupNode).groupId === node.groupId);
			if (index !== -1 && (nodes[index] as GroupNode).expanded) {
				const count = this.countDescendants(nodes, index);
				nodes.splice(index + 1, count);
				nodes[index] = { ...nodes[index], expanded: false, childrenLoaded: false } as GroupNode;
				this.treeNodes.set(nodes);
				await this.toggleExpand(nodes[index] as GroupNode);
			}
		} catch (err: any) {
			this.notificationService.openSnackbar(false, err.message, $localize`:@@AddCharger:Add Charger`);
		}
	}

	async openCreateDialog(event: MouseEvent, parentNode?: GroupNode) {
		event.stopPropagation();
		const ref = this.dialog.open(GroupDialogComponent, {
			width: DIALOG_WIDTH_SM,
			disableClose: true,
			autoFocus: 'first-tabbable',
			data: { mode: 'create', parentGroupName: parentNode?.name } as GroupDialogData,
		});

		const result = await firstValueFrom(ref.afterClosed());
		if (!result) return;

		try {
			await firstValueFrom(this.groupsService.createGroup(result.name, parentNode?.groupId ?? null));
			this.notificationService.openSnackbar(true, null, $localize`:@@CreateGroup:Create Group`);
			if (parentNode) {
				this.childrenCache.delete(parentNode.groupId);
				const nodes = [...this.treeNodes()];
				const index = nodes.findIndex(n => n.kind === 'group' && (n as GroupNode).groupId === parentNode.groupId);
				if (index !== -1 && (nodes[index] as GroupNode).expanded) {
					const count = this.countDescendants(nodes, index);
					nodes.splice(index + 1, count);
					nodes[index] = { ...nodes[index], expanded: false, childrenLoaded: false } as GroupNode;
					this.treeNodes.set(nodes);
					await this.toggleExpand(nodes[index] as GroupNode);
				}
			} else {
				this.loadGroups();
			}
		} catch (err: any) {
			this.notificationService.openSnackbar(false, err.message, $localize`:@@CreateGroup:Create Group`);
		}
	}

	async openEditDialog(event: MouseEvent, node: GroupNode) {
		event.preventDefault();
		event.stopPropagation();

		const ref = this.dialog.open(GroupDialogComponent, {
			width: DIALOG_WIDTH_SM,
			disableClose: true,
			autoFocus: 'first-tabbable',
			data: { mode: 'edit', group: node } as GroupDialogData,
		});

		const result = await firstValueFrom(ref.afterClosed());
		if (!result) return;

		const opResult = await firstValueFrom(this.groupsService.updateGroup(node.groupId, result.name));
		this.notificationService.openSnackbar(opResult.success, opResult.errorMessage, $localize`:@@UpdateGroup:Update group`);
		if (opResult.success) {
			this.treeNodes.set(this.treeNodes().map(n =>
				n.kind === 'group' && n.groupId === node.groupId ? { ...n, name: result.name } : n
			));
		}
	}

	async openDeleteDialog(event: MouseEvent, node: GroupNode) {
		event.preventDefault();
		event.stopPropagation();

		const ref = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: $localize`:@@DeleteGroupTitle:Delete group?`,
				message: $localize`:@@DeleteGroupMessage:Are you sure you want to delete group "${node.name}"?`,
				confirmText: $localize`:@@Delete:Delete`,
				cancelText: $localize`:@@Cancel:Cancel`,
			},
		});

		const confirmed = await firstValueFrom(ref.afterClosed());
		if (confirmed !== true) return;

		const result = await firstValueFrom(this.groupsService.deleteGroup(node.groupId));
		this.notificationService.openSnackbar(result.success, result.errorMessage, $localize`:@@DeleteGroup:Delete group`);
		if (result.success) {
			const nodes = [...this.treeNodes()];
			const index = nodes.findIndex(n => n.kind === 'group' && (n as GroupNode).groupId === node.groupId);
			if (index !== -1) {
				const count = this.countDescendants(nodes, index);
				nodes.splice(index, count + 1);
				this.treeNodes.set(nodes);
			}
			if (node.parentGroupId) {
				this.childrenCache.delete(node.parentGroupId);
			}
		}
	}

	async openDeleteChargerDialog(event: MouseEvent, node: ChargerNode) {
		event.preventDefault();
		event.stopPropagation();

		const ref = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: $localize`:@@DeleteChargerTitle:Delete charger?`,
				message: $localize`:@@DeleteChargerMessage:Are you sure you want to delete charger ${node.chargerId}?`,
				confirmText: $localize`:@@Delete:Delete`,
				cancelText: $localize`:@@Cancel:Cancel`,
			},
		});

		const confirmed = await firstValueFrom(ref.afterClosed());
		if (confirmed !== true) return;

		const result = await firstValueFrom(this.chargersService.deleteCharger(node.chargerId));
		this.notificationService.openSnackbar(result.success, result.errorMessage, $localize`:@@DeleteCharger:Delete Charger`);
		if (result.success) {
			this.chargersCache.delete(node.groupId);
			const nodes = [...this.treeNodes()];
			const index = nodes.findIndex(n => n.kind === 'charger' && n.chargerId === node.chargerId);
			if (index !== -1) nodes.splice(index, 1);
			this.treeNodes.set(nodes);
		}
	}

	async openMoveChargerDialog(event: MouseEvent, node: ChargerNode) {
		event.preventDefault();
		event.stopPropagation();

		const ref = this.dialog.open(MoveChargerDialogComponent, {
			width: DIALOG_WIDTH_SM,
			disableClose: true,
			data: { chargerId: node.chargerId, currentGroupId: node.groupId } as MoveChargerDialogData,
		});

		const result = await firstValueFrom(ref.afterClosed());
		if (!result) return;

		try {
			if (node.groupId !== this.UNGROUPED_ID) {
				const removeResult = await firstValueFrom(this.groupsService.removeChargerFromGroup(node.chargerId, node.groupId));
				if (!removeResult.success) {
					this.notificationService.openSnackbar(false, removeResult.errorMessage, $localize`:@@RemoveChargerFromGroup:Remove charger from group`);
					return;
				}
			}

			const assignResult = await firstValueFrom(this.groupsService.assignChargersToGroup(result.newGroupId, [node.chargerId]));
			if (!assignResult.success) {
				this.notificationService.openSnackbar(false, assignResult.errorMessage, $localize`:@@AssignChargerToGroup:Assign charger to group`);
				return;
			}

			this.notificationService.openSnackbar(true, null, $localize`:@@MoveCharger:Move Charger`);
			this.loadGroups();
		} catch (err: any) {
			this.notificationService.openSnackbar(false, err.message, $localize`:@@MoveCharger:Move Charger`);
		}
	}
}
